"use client";

import { useEffect, useMemo, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import {
  Conversation,
  ConversationContent,
  ConversationScrollButton,
} from "@/components/ai-elements/conversation";
import { PromptInput } from "@/components/ai-elements/prompt-input";
import { UserMessage } from "@/components/chat/user-message";
import { AssistantMessage } from "@/components/chat/assistant-message";
import { useImagineChat } from "@/providers/imagine-chat-provider";
import { InChatError } from "@/components/chat/in-chat-error";
import { useSendMessage } from "@/hooks/use-send-message";
import { imagineStore } from "@/stores/imagine.store";
import { PromptInputWrapper } from "./prompt-input-wrapper";
import { ScrollToBottomCapturer } from "./scroll-to-bottom-capturer";
import { Loader } from "@/components/chat/loader";
import { usePromptSubmitState } from "./use-prompt-submit-state";
import { PromptSubmitButton } from "./prompt-submit-button";
import { ChatSkeleton } from "./chat-skeleton";
import { ForkProjectCallout } from "./fork-project-callout";
import {
  ComposerAttachFileButton,
  ComposerChips,
  ComposerDropzone,
  ComposerFrame,
  ComposerProvider,
  ComposerTextarea,
  ComposerToolbar,
} from "../prompt-composer";
import { LoadMoreMessagesButton } from "./load-more-messages-button";
import { useFetchProject } from "@/hooks/use-fetch-project";
import { useMessageHistory } from "@/providers/message-history-provider";

function InChatLoader() {
  const scrollToBottom$ = imagineStore.use.chat.scrollToBottom$();
  useEffect(() => {
    setTimeout(() => {
      scrollToBottom$.next();
    }, 250);
  }, []);

  return (
    <div className="flex items-center">
      <Loader />
    </div>
  );
}

export function ChatPanel() {
  const projectId = imagineStore.use.projectId();
  const isReadonly = imagineStore.use.isReadonly();
  const addons = imagineStore.use.promptContext.addons();
  const chat = useImagineChat();
  const sendMessage = useSendMessage();
  const { error } = useImagineChat();
  const [prompt, setPrompt] = useState("");
  const [previousPrompt, setPreviousPrompt] = useState<string | null>(null);
  const trimmedPrompt = prompt.trim();
  const {
    hasMore: hasMoreMessagesInHistory,
    isLoading: isLoadingMessageHistory,
    isFetchingNextPage,
    fetchNextPage,
  } = useMessageHistory();

  const { isLoading: isLoadingProject } = useFetchProject();

  const {
    hasPendingMessage,
    hasUploadingAddons,
    isSubmitDisabled,
    isInputDisabled,
    showEmptyMessageTooltip,
  } = usePromptSubmitState({
    addons,
    trimmedPrompt,
    chatStatus: chat.status,
    isGenerating: chat.isGenerating,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitDisabled || chat.isGenerating || hasUploadingAddons) {
      return;
    }

    if (!hasPendingMessage) {
      return;
    }

    setPrompt("");
    setPreviousPrompt(null);

    await sendMessage({
      projectId: projectId!,
      text: trimmedPrompt.trim(),
    });
    chat.resumeStream({ projectId: projectId! });
  };

  const displayedMessages = useMemo(() => {
    const displayedMessages = chat.messages
      .filter(
        // Unique messages by ID
        (message, index, self) =>
          index === self.findIndex((t) => t.id === message.id),
      )
      .map((message, _index, filteredMessages) => {
        const isLatestMessage =
          message.id === filteredMessages[filteredMessages.length - 1]!.id;
        if (message.role === "user") {
          return <UserMessage key={message.id} message={message} />;
        }

        if (message.role === "assistant") {
          return (
            <AssistantMessage
              key={message.id}
              message={message}
              isLatestMessage={isLatestMessage}
            />
          );
        }
      });
    return displayedMessages;
  }, [chat.messages]);

  const showSkeleton = useMemo(() => {
    return isLoadingMessageHistory || isLoadingProject;
  }, [isLoadingMessageHistory, isLoadingProject]);

  const handleLoadMoreMessages = async () => {
    const { lockScroll, unlockScroll } = imagineStore.getState().chat;
    lockScroll();
    await fetchNextPage();
    // Wait 1s to ensure new messages are rendered
    await new Promise((resolve) => setTimeout(resolve, 1000));
    unlockScroll();
  };

  return (
    <div className="h-full w-full flex flex-col z-20">
      <Conversation className="flex-1">
        <ScrollToBottomCapturer />
        <ConversationContent className="space-y-12">
          {isReadonly && (
            <div className="-mx-4 -my-4">
              <ForkProjectCallout />
            </div>
          )}
          {hasMoreMessagesInHistory && (
            <LoadMoreMessagesButton
              onClick={() => {
                handleLoadMoreMessages();
              }}
              isLoading={isFetchingNextPage}
            />
          )}
          {showSkeleton ? <ChatSkeleton /> : displayedMessages}

          {error && <InChatError error={error} />}

          <AnimatePresence>
            {(chat.status === "submitted" || chat.status === "streaming") && (
              <motion.div
                className="flex items-center gap-2 mt-2 overflow-hidden"
                initial={{ height: "auto", opacity: 1 }}
                exit={{
                  height: 0,
                  opacity: 0,
                  marginTop: 0,
                  transition: {
                    duration: 0.2,
                    ease: "easeInOut",
                  },
                }}
              >
                <InChatLoader />
              </motion.div>
            )}
          </AnimatePresence>
        </ConversationContent>
        <ConversationScrollButton />
      </Conversation>

      <div className="p-1.5">
        <PromptInputWrapper isReadonly={isReadonly}>
          <PromptInput onSubmit={handleSubmit}>
            <ComposerProvider
              prompt={prompt}
              onPromptChange={setPrompt}
              previousPrompt={previousPrompt}
              onPreviousPromptChange={setPreviousPrompt}
            >
              <ComposerFrame>
                <ComposerDropzone>
                  <ComposerChips />
                  <ComposerTextarea
                    disabled={isInputDisabled}
                    className="min-h-[4lh] max-h-[8lh]"
                    placeholder={
                      isReadonly
                        ? "Fork this project to continue building..."
                        : "Reply..."
                    }
                  />
                  <ComposerToolbar>
                    <div></div>
                    <div className="flex items-center gap-2">
                      <ComposerAttachFileButton disabled={isReadonly} />
                      <PromptSubmitButton
                        status={chat.status}
                        disabled={isSubmitDisabled}
                        showEmptyTooltip={showEmptyMessageTooltip}
                        isGenerating={chat.isGenerating}
                      />
                    </div>
                  </ComposerToolbar>
                </ComposerDropzone>
              </ComposerFrame>
            </ComposerProvider>
          </PromptInput>
        </PromptInputWrapper>
      </div>
    </div>
  );
}
